/**
 * Enterprise Roster Data Models
 * Workforce management for aged care facilities
 * Covers awards, timesheets, shift marketplace, leave, on-call, and staffing demand
 */

// ---------------------------------------------------------------------------
// Constants & Enumerations
// ---------------------------------------------------------------------------

/** @type {Object<string, {code: string, label: string, loadingPercent: number, minEngagementHours: number}>} */
export const EMPLOYMENT_TYPES = {
  permanent_ft: {
    code: 'permanent_ft',
    label: 'Permanent Full-Time',
    loadingPercent: 0,
    minEngagementHours: 38
  },
  permanent_pt: {
    code: 'permanent_pt',
    label: 'Permanent Part-Time',
    loadingPercent: 0,
    minEngagementHours: 8
  },
  casual: {
    code: 'casual',
    label: 'Casual',
    loadingPercent: 25,
    minEngagementHours: 3
  },
  agency: {
    code: 'agency',
    label: 'Agency',
    loadingPercent: 0,
    minEngagementHours: 4
  },
  contractor: {
    code: 'contractor',
    label: 'Independent Contractor',
    loadingPercent: 0,
    minEngagementHours: 0
  }
};

/**
 * National Employment Standards + aged care specific leave types.
 * Entitlements expressed as hours per annum for a full-time (38 h/wk) employee.
 * @type {Object<string, {code: string, label: string, entitlementHoursPA: number|null, accrualMethod: string, paidLeave: boolean, awardClause: string, requiresMedCert: boolean, requiresMedCertAfterDays: number|null}>}
 */
export const LEAVE_TYPES = {
  annual: {
    code: 'annual',
    label: 'Annual Leave',
    entitlementHoursPA: 152,
    accrualMethod: 'continuous',
    paidLeave: true,
    awardClause: 'NES s87',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  personal: {
    code: 'personal',
    label: 'Personal / Carer\'s Leave',
    entitlementHoursPA: 76,
    accrualMethod: 'continuous',
    paidLeave: true,
    awardClause: 'NES s96',
    requiresMedCert: true,
    requiresMedCertAfterDays: 2
  },
  compassionate: {
    code: 'compassionate',
    label: 'Compassionate Leave',
    entitlementHoursPA: 15.2,
    accrualMethod: 'per_occasion',
    paidLeave: true,
    awardClause: 'NES s104',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  long_service: {
    code: 'long_service',
    label: 'Long Service Leave',
    entitlementHoursPA: null,
    accrualMethod: 'state_legislation',
    paidLeave: true,
    awardClause: 'State LSL Act',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  parental: {
    code: 'parental',
    label: 'Parental Leave',
    entitlementHoursPA: null,
    accrualMethod: 'per_occasion',
    paidLeave: false,
    awardClause: 'NES s67',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  community_service: {
    code: 'community_service',
    label: 'Community Service Leave',
    entitlementHoursPA: null,
    accrualMethod: 'per_occasion',
    paidLeave: false,
    awardClause: 'NES s108',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  workers_comp: {
    code: 'workers_comp',
    label: 'Workers\' Compensation',
    entitlementHoursPA: null,
    accrualMethod: 'as_required',
    paidLeave: true,
    awardClause: 'State WC Act',
    requiresMedCert: true,
    requiresMedCertAfterDays: 0
  },
  lwop: {
    code: 'lwop',
    label: 'Leave Without Pay',
    entitlementHoursPA: null,
    accrualMethod: 'not_applicable',
    paidLeave: false,
    awardClause: 'Enterprise Agreement',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  study: {
    code: 'study',
    label: 'Study Leave',
    entitlementHoursPA: 38,
    accrualMethod: 'annual_grant',
    paidLeave: true,
    awardClause: 'Enterprise Agreement',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  },
  toil: {
    code: 'toil',
    label: 'Time Off In Lieu',
    entitlementHoursPA: null,
    accrualMethod: 'earned',
    paidLeave: true,
    awardClause: 'Award cl.28',
    requiresMedCert: false,
    requiresMedCertAfterDays: null
  }
};

/**
 * Modern Award penalty-rate structures used in aged care.
 * Rates are expressed as multipliers of the base hourly rate.
 * @type {Object<string, {awardCode: string, awardName: string, effectiveDate: string, classifications: Object[], penalties: Object, overtime: Object, allowances: Object[]}>}
 */
export const AWARD_DEFINITIONS = {
  MA000034: {
    awardCode: 'MA000034',
    awardName: 'Nurses Award 2020',
    effectiveDate: '2024-07-01',
    classifications: [
      { level: 'RN1', payPoint: 1, annualSalary: 72446, hourlyBase: 36.58 },
      { level: 'RN1', payPoint: 2, annualSalary: 74372, hourlyBase: 37.55 },
      { level: 'RN1', payPoint: 3, annualSalary: 76298, hourlyBase: 38.52 },
      { level: 'RN1', payPoint: 4, annualSalary: 78510, hourlyBase: 39.64 },
      { level: 'RN1', payPoint: 5, annualSalary: 80436, hourlyBase: 40.61 },
      { level: 'RN1', payPoint: 6, annualSalary: 82648, hourlyBase: 41.72 },
      { level: 'RN1', payPoint: 7, annualSalary: 84860, hourlyBase: 42.84 },
      { level: 'RN1', payPoint: 8, annualSalary: 86786, hourlyBase: 43.81 },
      { level: 'RN2', payPoint: 1, annualSalary: 90338, hourlyBase: 45.60 },
      { level: 'RN2', payPoint: 2, annualSalary: 92550, hourlyBase: 46.72 },
      { level: 'RN2', payPoint: 3, annualSalary: 94762, hourlyBase: 47.83 },
      { level: 'RN2', payPoint: 4, annualSalary: 96974, hourlyBase: 48.95 },
      { level: 'RN3', payPoint: 1, annualSalary: 99472, hourlyBase: 50.21 },
      { level: 'RN3', payPoint: 2, annualSalary: 102256, hourlyBase: 51.62 },
      { level: 'RN3', payPoint: 3, annualSalary: 105040, hourlyBase: 53.02 },
      { level: 'EN1', payPoint: 1, annualSalary: 62504, hourlyBase: 31.56 },
      { level: 'EN1', payPoint: 2, annualSalary: 63946, hourlyBase: 32.28 },
      { level: 'EN1', payPoint: 3, annualSalary: 65388, hourlyBase: 33.01 },
      { level: 'EN1', payPoint: 4, annualSalary: 66830, hourlyBase: 33.73 },
      { level: 'EN1', payPoint: 5, annualSalary: 68272, hourlyBase: 34.46 }
    ],
    penalties: {
      saturday: 1.50,
      sunday: 1.75,
      publicHoliday: 2.50,
      eveningShift: 1.125,
      nightShift: 1.15,
      permanentAfternoon: 1.125,
      permanentNight: 1.15
    },
    overtime: {
      first2Hours: 1.50,
      after2Hours: 2.00,
      sundayOvertime: 2.00,
      publicHolidayOvertime: 2.50,
      restDayOvertime: 2.00
    },
    allowances: [
      { code: 'on_call_weekday', label: 'On-Call (Weekday)', amountPerShift: 18.68, isPercentage: false },
      { code: 'on_call_weekend', label: 'On-Call (Weekend/PH)', amountPerShift: 37.35, isPercentage: false },
      { code: 'uniform', label: 'Uniform Allowance', amountPerWeek: 7.29, isPercentage: false },
      { code: 'meal', label: 'Meal Allowance (Overtime)', amountPerOccasion: 16.91, isPercentage: false },
      { code: 'in_charge', label: 'In-Charge Allowance', amountPerShift: 24.42, isPercentage: false },
      { code: 'travel', label: 'Travel Between Facilities', amountPerKm: 0.91, isPercentage: false }
    ]
  },
  MA000018: {
    awardCode: 'MA000018',
    awardName: 'Aged Care Award 2010',
    effectiveDate: '2024-07-01',
    classifications: [
      { level: 'PCA1', payPoint: 1, annualSalary: 49070, hourlyBase: 24.77 },
      { level: 'PCA1', payPoint: 2, annualSalary: 50226, hourlyBase: 25.36 },
      { level: 'PCA1', payPoint: 3, annualSalary: 51382, hourlyBase: 25.94 },
      { level: 'PCA2', payPoint: 1, annualSalary: 52538, hourlyBase: 26.52 },
      { level: 'PCA2', payPoint: 2, annualSalary: 53694, hourlyBase: 27.11 },
      { level: 'PCA2', payPoint: 3, annualSalary: 54850, hourlyBase: 27.69 },
      { level: 'PCA3', payPoint: 1, annualSalary: 56006, hourlyBase: 28.27 },
      { level: 'PCA3', payPoint: 2, annualSalary: 57162, hourlyBase: 28.86 },
      { level: 'PCA3', payPoint: 3, annualSalary: 58318, hourlyBase: 29.44 },
      { level: 'PCA4', payPoint: 1, annualSalary: 60630, hourlyBase: 30.61 },
      { level: 'PCA4', payPoint: 2, annualSalary: 61786, hourlyBase: 31.19 },
      { level: 'PCA4', payPoint: 3, annualSalary: 62942, hourlyBase: 31.78 },
      { level: 'ADMIN1', payPoint: 1, annualSalary: 48500, hourlyBase: 24.49 },
      { level: 'ADMIN2', payPoint: 1, annualSalary: 51382, hourlyBase: 25.94 },
      { level: 'ADMIN3', payPoint: 1, annualSalary: 54264, hourlyBase: 27.40 }
    ],
    penalties: {
      saturday: 1.50,
      sunday: 1.75,
      publicHoliday: 2.25,
      eveningShift: 1.15,
      nightShift: 1.15,
      permanentAfternoon: 1.10,
      permanentNight: 1.15
    },
    overtime: {
      first2Hours: 1.50,
      after2Hours: 2.00,
      sundayOvertime: 2.00,
      publicHolidayOvertime: 2.50,
      restDayOvertime: 2.00
    },
    allowances: [
      { code: 'on_call_weekday', label: 'On-Call (Weekday)', amountPerShift: 14.53, isPercentage: false },
      { code: 'on_call_weekend', label: 'On-Call (Weekend/PH)', amountPerShift: 29.07, isPercentage: false },
      { code: 'uniform', label: 'Uniform Allowance', amountPerWeek: 6.42, isPercentage: false },
      { code: 'meal', label: 'Meal Allowance (Overtime)', amountPerOccasion: 15.76, isPercentage: false },
      { code: 'laundry', label: 'Laundry Allowance', amountPerWeek: 3.24, isPercentage: false },
      { code: 'travel', label: 'Travel Between Facilities', amountPerKm: 0.91, isPercentage: false }
    ]
  },
  MA000027: {
    awardCode: 'MA000027',
    awardName: 'Health Professionals and Support Services Award',
    effectiveDate: '2024-07-01',
    classifications: [
      { level: 'HP1', payPoint: 1, annualSalary: 54264, hourlyBase: 27.40 },
      { level: 'HP1', payPoint: 2, annualSalary: 56006, hourlyBase: 28.27 },
      { level: 'HP1', payPoint: 3, annualSalary: 57748, hourlyBase: 29.15 },
      { level: 'HP2', payPoint: 1, annualSalary: 62942, hourlyBase: 31.78 },
      { level: 'HP2', payPoint: 2, annualSalary: 65274, hourlyBase: 32.95 },
      { level: 'HP2', payPoint: 3, annualSalary: 67606, hourlyBase: 34.13 },
      { level: 'HP3', payPoint: 1, annualSalary: 70228, hourlyBase: 35.46 },
      { level: 'HP3', payPoint: 2, annualSalary: 72850, hourlyBase: 36.78 },
      { level: 'HP3', payPoint: 3, annualSalary: 75472, hourlyBase: 38.10 },
      { level: 'HP4', payPoint: 1, annualSalary: 78384, hourlyBase: 39.57 },
      { level: 'HP4', payPoint: 2, annualSalary: 81296, hourlyBase: 41.04 },
      { level: 'HP4', payPoint: 3, annualSalary: 84208, hourlyBase: 42.51 }
    ],
    penalties: {
      saturday: 1.50,
      sunday: 2.00,
      publicHoliday: 2.50,
      eveningShift: 1.125,
      nightShift: 1.15,
      permanentAfternoon: 1.125,
      permanentNight: 1.15
    },
    overtime: {
      first2Hours: 1.50,
      after2Hours: 2.00,
      sundayOvertime: 2.00,
      publicHolidayOvertime: 2.50,
      restDayOvertime: 2.00
    },
    allowances: [
      { code: 'on_call_weekday', label: 'On-Call (Weekday)', amountPerShift: 16.37, isPercentage: false },
      { code: 'on_call_weekend', label: 'On-Call (Weekend/PH)', amountPerShift: 32.74, isPercentage: false },
      { code: 'uniform', label: 'Uniform Allowance', amountPerWeek: 6.88, isPercentage: false },
      { code: 'meal', label: 'Meal Allowance (Overtime)', amountPerOccasion: 16.91, isPercentage: false },
      { code: 'travel', label: 'Travel Between Facilities', amountPerKm: 0.91, isPercentage: false }
    ]
  }
};

/**
 * Tiered broadcast order for unfilled shifts.
 * Each tier is tried in sequence; if no acceptance within `timeoutHours` the shift
 * escalates to the next tier.
 * @type {Object<string, {code: string, label: string, priority: number, timeoutHours: number, description: string}>}
 */
export const SHIFT_MARKETPLACE_TIERS = {
  float_pool: {
    code: 'float_pool',
    label: 'Facility Float Pool',
    priority: 1,
    timeoutHours: 2,
    description: 'Staff already assigned to this facility who are on the float pool roster'
  },
  casual_pool: {
    code: 'casual_pool',
    label: 'Casual Pool',
    priority: 2,
    timeoutHours: 4,
    description: 'Organisation-wide casual staff with availability'
  },
  pt_extras: {
    code: 'pt_extras',
    label: 'Part-Time Extra Shifts',
    priority: 3,
    timeoutHours: 6,
    description: 'Part-time staff who have opted into additional shift notifications'
  },
  ft_overtime: {
    code: 'ft_overtime',
    label: 'Full-Time Overtime',
    priority: 4,
    timeoutHours: 8,
    description: 'Full-time staff available to work overtime with manager approval'
  },
  marketplace_wide: {
    code: 'marketplace_wide',
    label: 'Organisation Marketplace',
    priority: 5,
    timeoutHours: 12,
    description: 'Broadcast to all eligible staff across every facility in the organisation'
  },
  agency: {
    code: 'agency',
    label: 'External Agency',
    priority: 6,
    timeoutHours: 24,
    description: 'Last-resort external agency placement'
  }
};

/** @type {Object<string, {code: string, label: string, editable: boolean, payrollVisible: boolean}>} */
export const TIMESHEET_STATUSES = {
  auto_generated: {
    code: 'auto_generated',
    label: 'Auto-Generated from Clock',
    editable: true,
    payrollVisible: false
  },
  pending_review: {
    code: 'pending_review',
    label: 'Pending Review',
    editable: true,
    payrollVisible: false
  },
  approved: {
    code: 'approved',
    label: 'Approved',
    editable: false,
    payrollVisible: true
  },
  exported: {
    code: 'exported',
    label: 'Exported to Payroll',
    editable: false,
    payrollVisible: true
  },
  locked: {
    code: 'locked',
    label: 'Locked (Pay Period Closed)',
    editable: false,
    payrollVisible: true
  }
};

/** @type {Object<string, {code: string, label: string, requiresApproval: boolean, severity: string}>} */
export const VARIANCE_TYPES = {
  none: { code: 'none', label: 'No Variance', requiresApproval: false, severity: 'info' },
  early_start: { code: 'early_start', label: 'Early Start', requiresApproval: true, severity: 'low' },
  late_start: { code: 'late_start', label: 'Late Start', requiresApproval: true, severity: 'medium' },
  early_finish: { code: 'early_finish', label: 'Early Finish', requiresApproval: true, severity: 'medium' },
  late_finish: { code: 'late_finish', label: 'Late Finish', requiresApproval: true, severity: 'low' },
  missed_break: { code: 'missed_break', label: 'Missed Break', requiresApproval: true, severity: 'high' },
  no_show: { code: 'no_show', label: 'No Show', requiresApproval: true, severity: 'critical' },
  unrostered: { code: 'unrostered', label: 'Unrostered Attendance', requiresApproval: true, severity: 'high' }
};

/** @type {Array<{level: number, label: string, notifies: string, timeoutMinutes: number, autoEscalate: boolean}>} */
export const ESCALATION_LEVELS = [
  { level: 1, label: 'Ward Coordinator', notifies: 'ward_coordinator', timeoutMinutes: 15, autoEscalate: true },
  { level: 2, label: 'Duty Nurse Manager', notifies: 'nurse_manager', timeoutMinutes: 30, autoEscalate: true },
  { level: 3, label: 'Facility Manager', notifies: 'facility_manager', timeoutMinutes: 60, autoEscalate: true },
  { level: 4, label: 'Regional Director / On-Call Exec', notifies: 'regional_director', timeoutMinutes: 120, autoEscalate: false }
];

// ---------------------------------------------------------------------------
// ID Helper
// ---------------------------------------------------------------------------

const _uid = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// ---------------------------------------------------------------------------
// Factory Functions
// ---------------------------------------------------------------------------

/**
 * Extended staff profile for enterprise rostering.
 * Supplements the base StaffProfile with multi-facility, marketplace, and award fields.
 * @param {Object} data - Override values
 * @returns {Object} EnterpriseStaffProfile
 */
export const createEnterpriseStaffProfile = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('estaff'),
    staffId: data.staffId || null,
    primaryFacilityId: data.primaryFacilityId || null,
    authorisedFacilityIds: data.authorisedFacilityIds || [],
    floatPool: data.floatPool ?? false,
    contractedHoursPerWeek: data.contractedHoursPerWeek ?? 38,
    maxHoursPerWeek: data.maxHoursPerWeek ?? 48,
    homeWardPreferences: data.homeWardPreferences || [],
    travelRadiusKm: data.travelRadiusKm ?? 25,
    agencyDetails: data.agencyDetails || {
      agencyName: null,
      agencyContactPhone: null,
      hourlyRate: null,
      contractExpiry: null
    },
    poolPriority: data.poolPriority ?? 5,
    reliabilityScore: data.reliabilityScore ?? 100,
    shiftsCompleted: data.shiftsCompleted ?? 0,
    shiftsCancelled: data.shiftsCancelled ?? 0,
    totalMarketplaceShifts: data.totalMarketplaceShifts ?? 0,
    onCallAvailable: data.onCallAvailable ?? false,
    classificationLevel: data.classificationLevel || null,
    payPoint: data.payPoint ?? 1,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * Facility-level staffing configuration.
 * Holds bed counts, occupancy targets, care-minute benchmarks, and minimum-staff rules.
 * @param {Object} data - Override values
 * @returns {Object} FacilityStaffingProfile
 */
export const createFacilityStaffingProfile = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('fsp'),
    facilityId: data.facilityId || null,
    facilityName: data.facilityName || '',
    totalBeds: data.totalBeds ?? 0,
    currentOccupancy: data.currentOccupancy ?? 0,
    targetCareMinutesPerDay: data.targetCareMinutesPerDay ?? 215,
    targetRNMinutesPerDay: data.targetRNMinutesPerDay ?? 44,
    enSubstitutionPercentage: data.enSubstitutionPercentage ?? 10,
    minimumStaffPerShift: data.minimumStaffPerShift || {
      am: { rn: 2, en: 2, pca: 6 },
      pm: { rn: 2, en: 1, pca: 5 },
      night: { rn: 1, en: 1, pca: 3 }
    },
    wardProfiles: data.wardProfiles || [],
    publicHolidayCalendar: data.publicHolidayCalendar || [],
    region: data.region || '',
    stateJurisdiction: data.stateJurisdiction || 'VIC',
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * Leave request lifecycle.
 * @param {Object} data - Override values
 * @returns {Object} LeaveRequest
 */
export const createLeaveRequest = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('leave'),
    staffId: data.staffId || null,
    staffName: data.staffName || '',
    leaveType: data.leaveType || 'annual',
    startDate: data.startDate || null,
    endDate: data.endDate || null,
    isPartialDay: data.isPartialDay ?? false,
    partialDayHours: data.partialDayHours ?? null,
    status: data.status || 'pending',
    requestedAt: data.requestedAt || now,
    requestedBy: data.requestedBy || null,
    approvedAt: data.approvedAt || null,
    approvedBy: data.approvedBy || null,
    declinedReason: data.declinedReason || null,
    medicalCertificateAttached: data.medicalCertificateAttached ?? false,
    medicalCertificateDocumentId: data.medicalCertificateDocumentId || null,
    balanceBefore: data.balanceBefore ?? null,
    balanceAfter: data.balanceAfter ?? null,
    hoursDeducted: data.hoursDeducted ?? 0,
    rosterImpact: data.rosterImpact || {
      shiftsAffected: [],
      backfillRequired: false,
      backfillTaskId: null
    },
    blackoutOverride: data.blackoutOverride ?? false,
    blackoutOverrideApprovedBy: data.blackoutOverrideApprovedBy || null,
    payrollExported: data.payrollExported ?? false,
    notes: data.notes || ''
  };
};

/**
 * Timesheet entry derived from clock-in/out, reconciled against rostered times.
 * @param {Object} data - Override values
 * @returns {Object} TimesheetEntry
 */
export const createTimesheetEntry = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('ts'),
    staffId: data.staffId || null,
    staffName: data.staffName || '',
    shiftId: data.shiftId || null,
    payPeriodId: data.payPeriodId || null,
    clockInTime: data.clockInTime || null,
    clockOutTime: data.clockOutTime || null,
    breakStartTime: data.breakStartTime || null,
    breakEndTime: data.breakEndTime || null,
    rosteredStart: data.rosteredStart || null,
    rosteredEnd: data.rosteredEnd || null,
    varianceType: data.varianceType || 'none',
    varianceMinutes: data.varianceMinutes ?? 0,
    varianceApprovedBy: data.varianceApprovedBy || null,
    varianceApprovedAt: data.varianceApprovedAt || null,
    ordinaryHours: data.ordinaryHours ?? 0,
    overtimeHours: data.overtimeHours ?? 0,
    penaltyHours: data.penaltyHours ?? 0,
    awardApplied: data.awardApplied || null,
    classificationLevel: data.classificationLevel || null,
    payPoint: data.payPoint ?? 1,
    basePay: data.basePay ?? 0,
    penaltyPay: data.penaltyPay ?? 0,
    overtimePay: data.overtimePay ?? 0,
    casualLoading: data.casualLoading ?? 0,
    allowances: data.allowances || {},
    totalGross: data.totalGross ?? 0,
    superannuation: data.superannuation ?? 0,
    workerComp: data.workerComp ?? 0,
    payrollTax: data.payrollTax ?? 0,
    status: data.status || TIMESHEET_STATUSES.auto_generated.code,
    approvedBy: data.approvedBy || null,
    approvedAt: data.approvedAt || null,
    exportedAt: data.exportedAt || null,
    exportBatchId: data.exportBatchId || null,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * Shift posted to the internal marketplace for backfill.
 * @param {Object} data - Override values
 * @returns {Object} MarketplaceShift
 */
export const createMarketplaceShift = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('mkt'),
    shiftId: data.shiftId || null,
    facilityId: data.facilityId || null,
    facilityName: data.facilityName || '',
    postedAt: data.postedAt || now,
    postedBy: data.postedBy || null,
    expiresAt: data.expiresAt || null,
    tier: data.tier || SHIFT_MARKETPLACE_TIERS.float_pool.code,
    currentTier: data.currentTier || SHIFT_MARKETPLACE_TIERS.float_pool.code,
    tierEscalatedAt: data.tierEscalatedAt || null,
    applicants: data.applicants || [],
    selectedStaffId: data.selectedStaffId || null,
    selectedAt: data.selectedAt || null,
    selectedBy: data.selectedBy || null,
    agencyOffered: data.agencyOffered ?? false,
    agencyId: data.agencyId || null,
    agencyRate: data.agencyRate ?? null,
    travelAllowanceApplied: data.travelAllowanceApplied ?? false,
    travelDistance: data.travelDistance ?? 0,
    travelAllowanceAmount: data.travelAllowanceAmount ?? 0,
    costComparison: data.costComparison || {
      internalCost: 0,
      agencyCost: 0,
      savings: 0
    },
    status: data.status || 'open',
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * On-call roster entry with callout tracking.
 * @param {Object} data - Override values
 * @returns {Object} OnCallEntry
 */
export const createOnCallEntry = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('oncall'),
    staffId: data.staffId || null,
    staffName: data.staffName || '',
    facilityId: data.facilityId || null,
    startDateTime: data.startDateTime || null,
    endDateTime: data.endDateTime || null,
    type: data.type || 'weekday',
    allowanceRate: data.allowanceRate ?? 0,
    callOuts: data.callOuts || [],
    status: data.status || 'scheduled',
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

/**
 * Escalation event triggered when a critical gap cannot be filled within SLA.
 * @param {Object} data - Override values
 * @returns {Object} EscalationEvent
 */
export const createEscalationEvent = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || _uid('esc'),
    facilityId: data.facilityId || null,
    triggeredAt: data.triggeredAt || now,
    reason: data.reason || '',
    escalationChain: data.escalationChain || ESCALATION_LEVELS.map(l => ({
      level: l.level,
      label: l.label,
      notifiedAt: null,
      respondedAt: null,
      respondedBy: null,
      response: null
    })),
    currentLevel: data.currentLevel ?? 1,
    respondedBy: data.respondedBy || null,
    respondedAt: data.respondedAt || null,
    responseTime: data.responseTime ?? null,
    outcome: data.outcome || null,
    resolvedAt: data.resolvedAt || null,
    notes: data.notes || '',
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now
  };
};

// ---------------------------------------------------------------------------
// Calculation Engines
// ---------------------------------------------------------------------------

/**
 * Calculate gross pay breakdown for a given award, classification, and shift context.
 *
 * @param {Object} params
 * @param {string} params.awardCode - e.g. 'MA000034'
 * @param {string} params.classificationLevel - e.g. 'RN1'
 * @param {number} params.payPoint - e.g. 3
 * @param {number} params.hoursWorked
 * @param {string} params.shiftType - 'day' | 'evening' | 'night'
 * @param {string} params.dayType - 'weekday' | 'saturday' | 'sunday' | 'publicHoliday'
 * @param {boolean} [params.isOvertimeFirst2=false]
 * @param {boolean} [params.isOvertimeAfter=false]
 * @param {boolean} [params.isCasual=false]
 * @param {boolean} [params.isAgency=false]
 * @param {Array}  [params.allowances=[]] - array of allowance codes to apply
 * @returns {{basePay: number, penaltyPay: number, overtimePay: number, casualLoading: number, allowancePay: number, totalGross: number, superannuation: number, employerCost: number}}
 */
export const calculateAwardPay = (params = {}) => {
  const {
    awardCode,
    classificationLevel,
    payPoint = 1,
    hoursWorked = 0,
    shiftType = 'day',
    dayType = 'weekday',
    isOvertimeFirst2 = false,
    isOvertimeAfter = false,
    isCasual = false,
    isAgency = false,
    allowances = []
  } = params;

  const award = AWARD_DEFINITIONS[awardCode];
  if (!award) {
    console.warn(`[calculateAwardPay] Unknown award code: ${awardCode}`);
    return _emptyPayResult();
  }

  const classification = award.classifications.find(
    c => c.level === classificationLevel && c.payPoint === payPoint
  );
  if (!classification) {
    console.warn(`[calculateAwardPay] Classification ${classificationLevel} PP${payPoint} not found in ${awardCode}`);
    return _emptyPayResult();
  }

  const hourlyBase = classification.hourlyBase;
  const SUPER_RATE = 0.115;
  const CASUAL_LOADING = 0.25;

  let basePay = 0;
  let penaltyPay = 0;
  let overtimePay = 0;
  let casualLoading = 0;

  // Overtime takes precedence over ordinary penalty logic
  if (isOvertimeFirst2 || isOvertimeAfter) {
    const otMultiplier = isOvertimeAfter
      ? (award.overtime.after2Hours || 2.0)
      : (award.overtime.first2Hours || 1.5);

    if (dayType === 'sunday') {
      overtimePay = hoursWorked * hourlyBase * (award.overtime.sundayOvertime || 2.0);
    } else if (dayType === 'publicHoliday') {
      overtimePay = hoursWorked * hourlyBase * (award.overtime.publicHolidayOvertime || 2.5);
    } else {
      overtimePay = hoursWorked * hourlyBase * otMultiplier;
    }
    basePay = 0;
  } else {
    basePay = hoursWorked * hourlyBase;

    const penaltyMap = {
      weekday: {
        day: 1.0,
        evening: award.penalties.eveningShift || 1.0,
        night: award.penalties.nightShift || 1.0
      },
      saturday: { day: award.penalties.saturday, evening: award.penalties.saturday, night: award.penalties.saturday },
      sunday: { day: award.penalties.sunday, evening: award.penalties.sunday, night: award.penalties.sunday },
      publicHoliday: { day: award.penalties.publicHoliday, evening: award.penalties.publicHoliday, night: award.penalties.publicHoliday }
    };

    const multiplier = penaltyMap[dayType]?.[shiftType] ?? 1.0;
    if (multiplier > 1.0) {
      penaltyPay = hoursWorked * hourlyBase * (multiplier - 1.0);
    }
  }

  if (isCasual && !isAgency) {
    casualLoading = (basePay + penaltyPay) * CASUAL_LOADING;
  }

  let allowancePay = 0;
  for (const code of allowances) {
    const def = award.allowances.find(a => a.code === code);
    if (def) {
      allowancePay += def.amountPerShift || def.amountPerOccasion || def.amountPerWeek || 0;
    }
  }

  const totalGross = _round(basePay + penaltyPay + overtimePay + casualLoading + allowancePay);
  const superannuation = _round(totalGross * SUPER_RATE);
  const employerCost = _round(totalGross + superannuation);

  return {
    basePay: _round(basePay),
    penaltyPay: _round(penaltyPay),
    overtimePay: _round(overtimePay),
    casualLoading: _round(casualLoading),
    allowancePay: _round(allowancePay),
    totalGross,
    superannuation,
    employerCost
  };
};

/** @returns {Object} zeroed pay result */
const _emptyPayResult = () => ({
  basePay: 0, penaltyPay: 0, overtimePay: 0, casualLoading: 0,
  allowancePay: 0, totalGross: 0, superannuation: 0, employerCost: 0
});

/** Round to 2 dp */
const _round = (n) => Math.round(n * 100) / 100;

/**
 * Demand-driven staffing calculator.
 * Uses AN-ACC distribution, clinical escalations, and other factors to derive
 * per-shift staff requirements aligned with the 215/44 care-minute targets.
 *
 * @param {Object} params
 * @param {number} params.occupancy - current resident count
 * @param {Object} [params.anaccDistribution] - { highNeed: n, lowNeed: n } counts
 * @param {number} [params.clinicalEscalations=0] - active clinical escalation count
 * @param {number} [params.oneToOneObservations=0]
 * @param {number} [params.palliativeActive=0]
 * @param {number} [params.twoPersonAssists=0]
 * @param {number} [params.newAdmissions=0]
 * @param {number} [params.shiftLengthMinutes=480]
 * @returns {{baseCareMinutes: number, baseRNMinutes: number, adjustedCareMinutes: number, adjustedRNMinutes: number, requiredRN: number, requiredEN: number, requiredPCA: number, totalRequired: number}}
 */
export const calculateDemand = (params = {}) => {
  const {
    occupancy = 0,
    anaccDistribution = null,
    clinicalEscalations = 0,
    oneToOneObservations = 0,
    palliativeActive = 0,
    twoPersonAssists = 0,
    newAdmissions = 0,
    shiftLengthMinutes = 480
  } = params;

  const TARGET_TOTAL_MINUTES = 215;
  const TARGET_RN_MINUTES = 44;

  const baseCareMinutes = occupancy * TARGET_TOTAL_MINUTES;
  const baseRNMinutes = occupancy * TARGET_RN_MINUTES;

  // Acuity weighting: high-need residents consume ~30 % more care minutes
  let acuityMultiplier = 1.0;
  if (anaccDistribution && occupancy > 0) {
    const highNeed = anaccDistribution.highNeed || 0;
    const highNeedRatio = highNeed / occupancy;
    acuityMultiplier = 1.0 + (highNeedRatio * 0.3);
  }

  const ESCALATION_MINUTES = 60;
  const ONE_TO_ONE_MINUTES_PER_SHIFT = shiftLengthMinutes;
  const PALLIATIVE_MINUTES = 120;
  const TWO_PERSON_EXTRA_MINUTES = 30;
  const NEW_ADMISSION_MINUTES = 90;

  const supplementaryMinutes =
    (clinicalEscalations * ESCALATION_MINUTES) +
    (oneToOneObservations * ONE_TO_ONE_MINUTES_PER_SHIFT) +
    (palliativeActive * PALLIATIVE_MINUTES) +
    (twoPersonAssists * TWO_PERSON_EXTRA_MINUTES) +
    (newAdmissions * NEW_ADMISSION_MINUTES);

  const adjustedCareMinutes = Math.ceil(baseCareMinutes * acuityMultiplier + supplementaryMinutes);

  // RN minutes scale proportionally plus additional RN time for clinical events
  const rnClinicalExtra = (clinicalEscalations * 30) + (palliativeActive * 45) + (newAdmissions * 40);
  const adjustedRNMinutes = Math.ceil(baseRNMinutes * acuityMultiplier + rnClinicalExtra);

  const shiftsPerDay = (24 * 60) / shiftLengthMinutes;

  // Staff needed per shift (care minutes for the day divided by shifts × shift length)
  const totalStaffMinutesPerShift = adjustedCareMinutes / shiftsPerDay;
  const rnMinutesPerShift = adjustedRNMinutes / shiftsPerDay;

  const requiredRN = Math.max(1, Math.ceil(rnMinutesPerShift / shiftLengthMinutes));
  const remainingMinutes = totalStaffMinutesPerShift - (requiredRN * shiftLengthMinutes);
  const enShare = 0.3;
  const requiredEN = Math.max(0, Math.ceil((remainingMinutes * enShare) / shiftLengthMinutes));
  const requiredPCA = Math.max(0, Math.ceil((remainingMinutes * (1 - enShare)) / shiftLengthMinutes));
  const totalRequired = requiredRN + requiredEN + requiredPCA;

  return {
    baseCareMinutes,
    baseRNMinutes,
    adjustedCareMinutes,
    adjustedRNMinutes,
    requiredRN,
    requiredEN,
    requiredPCA,
    totalRequired
  };
};

// ---------------------------------------------------------------------------
// Default Export
// ---------------------------------------------------------------------------

export default {
  EMPLOYMENT_TYPES,
  LEAVE_TYPES,
  AWARD_DEFINITIONS,
  SHIFT_MARKETPLACE_TIERS,
  TIMESHEET_STATUSES,
  VARIANCE_TYPES,
  ESCALATION_LEVELS,
  createEnterpriseStaffProfile,
  createFacilityStaffingProfile,
  createLeaveRequest,
  createTimesheetEntry,
  createMarketplaceShift,
  createOnCallEntry,
  createEscalationEvent,
  calculateAwardPay,
  calculateDemand
};
